// src/services/salesService.ts

import api from './api';
import {
    SalesResult,
    SalesListResponse,
    SalesCalculateRequest,
    SalesCalculateResponse,
    SalesStatusUpdateRequest,
    SalesSearchParams,
    SalesReportRequest,
    SalesReportResponse,
    SalesStatus,
    // 新しい型のインポート
    SupervisorAllowance,
    SupervisorAllowanceSummary,
    SupervisorAllowanceListResponse,
    SupervisorAllowanceSummaryResponse,
    SupervisorAllowanceSearchParams,
    CalculationDateResponse
} from '../types/sales';

export const salesService = {
    /**
     * 売上一覧を取得
     */
    getAll: async (params?: SalesSearchParams, token?: string): Promise<SalesResult[]> => {
        try {
            // クエリパラメータを構築
            const queryParams = new URLSearchParams();
            if (params) {
                Object.entries(params).forEach(([key, value]) => {
                    if (value !== undefined && value !== null && value !== '') {
                        queryParams.append(key, String(value));
                    }
                });
            }

            // 認証ヘッダーを設定
            const headers: Record<string, string> = {};
            if (token) {
                headers['Authorization'] = `Bearer ${token}`;
            }

            const response = await api.get<SalesListResponse>(`/sales/list?${queryParams}`, { headers });

            console.log('売上データAPI応答:', response.data);

            // レスポンス形式を確認し、適切にデータを抽出
            if (response.data.success) {
                // 成功レスポンスの場合
                return response.data.salesResults || [];
            } else {
                throw new Error(response.data.message || '売上データの取得に失敗しました');
            }
        } catch (error) {
            console.error('売上データ一覧の取得に失敗:', error);
            throw new Error('売上データの取得に失敗しました');
        }
    },

    /**
     * 売上集計処理の実行
     */
    calculateSales: async (request: SalesCalculateRequest, token?: string): Promise<SalesCalculateResponse> => {
        try {
            // 認証ヘッダーを設定
            const headers: Record<string, string> = {};
            if (token) {
                headers['Authorization'] = `Bearer ${token}`;
            }

            console.log('集計リクエスト:', request); // デバッグ用

            const response = await api.post<SalesCalculateResponse>('/sales/calculate', request, { headers });

            if (!response.data.success) {
                throw new Error(response.data.message || '売上集計処理に失敗しました');
            }

            return response.data;
        } catch (error) {
            console.error('売上集計処理の実行に失敗:', error);
            throw error;
        }
    },

    /**
     * 売上ステータスの更新
     */
    updateStatus: async (id: number, request: SalesStatusUpdateRequest, token?: string): Promise<boolean> => {
        try {
            // 認証ヘッダーを設定
            const headers: Record<string, string> = {};
            if (token) {
                headers['Authorization'] = `Bearer ${token}`;
            }

            const response = await api.put<{ success: boolean; message?: string }>(
                `/sales/${id}/status`,
                request,
                { headers }
            );

            if (!response.data.success) {
                throw new Error(response.data.message || '売上ステータスの更新に失敗しました');
            }

            return true;
        } catch (error) {
            console.error('売上ステータスの更新に失敗:', error);
            throw error;
        }
    },

    /**
     * 売上レポートの取得
     */
    getReport: async (request: SalesReportRequest, token?: string): Promise<SalesReportResponse> => {
        try {
            // 認証ヘッダーを設定
            const headers: Record<string, string> = {};
            if (token) {
                headers['Authorization'] = `Bearer ${token}`;
            }

            // GETリクエストでパラメータを送信
            const queryParams = new URLSearchParams();
            queryParams.append('period', request.period);
            queryParams.append('targetDate', request.targetDate);

            const response = await api.get<SalesReportResponse>(`/sales/report?${queryParams}`, { headers });

            if (!response.data.success) {
                throw new Error(response.data.message || '売上レポートの取得に失敗しました');
            }

            return response.data;
        } catch (error) {
            console.error('売上レポートの取得に失敗:', error);
            throw error;
        }
    },

    /**
     * 対象月の売上データが存在するか確認
     */
    checkMonthExists: async (month: string, token?: string): Promise<boolean> => {
        try {
            // 認証ヘッダーを設定
            const headers: Record<string, string> = {};
            if (token) {
                headers['Authorization'] = `Bearer ${token}`;
            }

            // デバッグ情報
            console.log(`対象月の確認: ${month}`);

            const response = await api.get<{ success: boolean; exists: boolean; message?: string }>(
                `/sales/check-month?month=${month}`,
                { headers }
            );

            if (!response.data.success) {
                throw new Error(response.data.message || '売上データ確認に失敗しました');
            }

            return response.data.exists;
        } catch (error) {
            console.error('対象月の売上データ確認に失敗:', error);
            // 既存データがない場合も正常なケースとして扱う
            return false;
        }
    },

    /**
     * 経理担当者IDの確認
     */
    validateClerkId: async (clerkId: number, token?: string): Promise<boolean> => {
        try {
            // 認証ヘッダーを設定
            const headers: Record<string, string> = {};
            if (token) {
                headers['Authorization'] = `Bearer ${token}`;
            }

            const response = await api.get<{ success: boolean; isValid: boolean; message?: string }>(
                `/sales/validate-clerk?clerkId=${clerkId}`,
                { headers }
            );

            if (!response.data.success) {
                throw new Error(response.data.message || '経理担当者の検証に失敗しました');
            }

            return response.data.isValid;
        } catch (error) {
            console.error('経理担当者IDの検証に失敗:', error);
            return false; // エラー時は無効と返す
        }
    },

    /**
     * 特定の年月の職長手当データを取得
     */
    getSupervisorAllowancesByMonth: async (yearMonth: string, token?: string): Promise<SupervisorAllowance[]> => {
        try {
            // 認証ヘッダーを設定
            const headers: Record<string, string> = {};
            if (token) {
                headers['Authorization'] = `Bearer ${token}`;
            }

            console.log(`職長手当データ取得: ${yearMonth}`); // デバッグ用

            const response = await api.get<SupervisorAllowanceListResponse>(
                `/sales/supervisor-allowance/by-month/${yearMonth}`,
                { headers }
            );

            console.log('職長手当データAPI応答:', response.data); // デバッグ用

            if (!response.data.success) {
                throw new Error(response.data.message || '職長手当データの取得に失敗しました');
            }

            return response.data.allowances || [];
        } catch (error) {
            console.error('職長手当データの取得に失敗:', error);
            throw new Error('職長手当データの取得に失敗しました');
        }
    },

    /**
     * 特定の年月の職長手当集計データを取得
     */
    getSupervisorAllowanceSummaryByMonth: async (yearMonth: string, token?: string): Promise<SupervisorAllowanceSummary[]> => {
        try {
            // 認証ヘッダーを設定
            const headers: Record<string, string> = {};
            if (token) {
                headers['Authorization'] = `Bearer ${token}`;
            }

            console.log(`職長手当集計データ取得: ${yearMonth}`); // デバッグ用

            const response = await api.get<SupervisorAllowanceSummaryResponse>(
                `/sales/supervisor-allowance/summary-by-month/${yearMonth}`,
                { headers }
            );

            console.log('職長手当集計データAPI応答:', response.data); // デバッグ用

            if (!response.data.success) {
                throw new Error(response.data.message || '職長手当集計データの取得に失敗しました');
            }

            return response.data.summary || [];
        } catch (error) {
            console.error('職長手当集計データの取得に失敗:', error);
            throw new Error('職長手当集計データの取得に失敗しました');
        }
    },


    /**
   * 特定の年月の集計日（最終更新日）を取得
   */
    getCalculationDate: async (yearMonth: string, token?: string): Promise<string | null> => {
        try {
            // 認証ヘッダーを設定
            const headers: Record<string, string> = {};
            if (token) {
                headers['Authorization'] = `Bearer ${token}`;
            }

            console.log(`集計日データ取得: ${yearMonth}`); // デバッグ用

            const response = await api.get<CalculationDateResponse>(
                `/sales/calculation-date/${yearMonth}`,
                { headers }
            );

            console.log('集計日データAPI応答:', response.data); // デバッグ用

            if (!response.data.success) {
                throw new Error('集計日の取得に失敗しました');
            }

            return response.data.calculationDate;
        } catch (error) {
            console.error('集計日の取得に失敗:', error);
            return null; // エラー時はnullを返す
        }
    }
};




export default salesService;